package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlin.random.Random
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

val LDisclosureStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
        }
}

/**
 * A real `<button>`, and that is the whole point of this component existing.
 *
 * The group headers it replaces were `<div>`s with an `onClick`: no keyboard activation, no focus
 * ring, no announced state — a reader on a keyboard could not open a QA group at all, and one on a
 * screen reader was never told the group had anything inside it. `aria-expanded` and
 * `aria-controls` are the pair that says both things, and a `<button>` is what gets Enter and Space
 * for free rather than through a `keydown` handler that has to be written correctly twice.
 *
 * `text-align: start` because a full-width button centres its label by default, and a heading that
 * drifts to the middle of the row is not a heading any more.
 */
val LDisclosureHeaderStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .minHeight(HIT_AREA_FLOOR_PX.px)
            .backgroundColor(Color.rgba(0, 0, 0, 0f))
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, Color.rgba(0, 0, 0, 0f))
            .borderRadius(Radius.Control)
            .padding(topBottom = Space.SM, leftRight = Space.SM)
            .gap(Space.SM)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .textAlign(TextAlign.Start)
            .cursor(Cursor.Pointer)
            .alignItems(AlignItems.Center)
            .styleModifier { property("display", "flex") }
            .transitionColors()
    }
    cssRule(":hover") {
        Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/**
 * The caret, turned rather than swapped.
 *
 * One glyph rotating 180° over [Motion.FastMs] rather than a chevron-down exchanged for a
 * chevron-up: an exchange is a jump cut, and the direction of travel is the only thing this mark
 * has to communicate. `transform` only — the rotation costs nothing and moves no layout.
 */
val LDisclosureCaretStyle = CssStyle {
    base {
        Modifier
            .color(colorMode.toSitePalette().textSecondary)
            .styleModifier {
                property("display", "inline-flex")
                property("margin-inline-start", "auto")
                property("transform", "rotate(0deg)")
                property("transition", "transform ${Motion.FastMs}ms ${Motion.EaseOut}")
            }
    }
    cssRule("[data-expanded=\"true\"]") {
        Modifier.styleModifier { property("transform", "rotate(180deg)") }
    }
}

val LDisclosurePanelStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .padding(top = Space.SM)
}

/**
 * A titled section that opens and closes.
 *
 * The panel element is **always in the DOM** and carries `hidden` when collapsed, while its
 * children are composed only while open. That combination is deliberate: `aria-controls` on the
 * header has to point at an element that exists, or assistive technology reports a broken
 * reference and the reader is told nothing about what the button controls — but composing a
 * collapsed group's contents anyway would mount every QA issue on the page at once.
 *
 * @param badge a count or state to sit beside the title — an `LBadge`, typically.
 * @param icon the section's own mark, leading.
 */
@Composable
fun LDisclosure(
    title: String,
    expanded: Boolean,
    onExpandedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    badge: (@Composable () -> Unit)? = null,
    icon: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    val groupId = remember { "lingvo-disclosure-${Random.nextInt(1_000_000)}" }
    val headerId = "$groupId-header"
    val panelId = "$groupId-panel"

    Div(LDisclosureStyle.toModifier().then(modifier).toAttrs { attr("data-disclosure", "") }) {
        // A native `<button>`, not a `<div role="button">`. The role attribute buys the
        // announcement and none of the behaviour: Space still scrolls the page, Enter still does
        // nothing, and the control is not in the tab order at all. Every one of those has to be
        // hand-written, correctly, on every disclosure in the product — or written once by using
        // the element the platform already implements.
        Button(
            LDisclosureHeaderStyle.toModifier().toAttrs {
                type(ButtonType.Button)
                id(headerId)
                attr("aria-expanded", expanded.toString())
                attr("aria-controls", panelId)
                onClick { onExpandedChange(!expanded) }
            },
        ) {
            if (icon != null) {
                Span(
                    Modifier
                        .iconSize(IconSize.MD)
                        .color(ColorMode.current.toSitePalette().textSecondary)
                        .styleModifier { property("display", "inline-flex") }
                        .toAttrs(),
                ) {
                    icon()
                }
            }
            Text(title)
            badge?.invoke()
            Span(
                LDisclosureCaretStyle.toModifier()
                    .iconSize(IconSize.MD)
                    .toAttrs {
                        attr("data-disclosure-caret", "")
                        // The style keys off this, so it is the attribute and not a second
                        // inline `transform` that decides which way the caret points.
                        attr("data-expanded", expanded.toString())
                    },
            ) {
                LChevronDownIcon(Modifier.iconSize(IconSize.MD))
            }
        }
        Div(
            LDisclosurePanelStyle.toModifier().toAttrs {
                id(panelId)
                attr("role", "region")
                attr("aria-labelledby", headerId)
                if (!expanded) attr("hidden", "")
            },
        ) {
            if (expanded) content()
        }
    }
}
